import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';
import type { Project, ProjectWithRole, ProjectWithStats, ProjectStats, ProjectSettings, TaskRow, KanbanColumn, TeamMember } from '../types';

// O que a página pública recebe numa única ida ao servidor. `passwordRequired` e `name`
// são o caminho da tela de senha, onde ainda não há cronograma para entregar.
export interface SharedProjectPayload {
  project?: Record<string, unknown>;
  tasks?: TaskRow[];
  /**
   * Só vem preenchida quando quem publicou ligou "Mostrar responsáveis".
   * `tasks.assigned` guarda ora o id, ora o nome do membro (espelho TEXT sem FK),
   * então as telas precisam desta lista para resolver o rótulo.
   */
  team?: TeamMember[];
  kanbanColumns?: KanbanColumn[] | null;
  showAssignees?: boolean;
  passwordRequired?: boolean;
  name?: string;
}

export const projectService = {
  // Buscar todos os projetos (para admins ou fallback)
  async getAll(): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Project[];
  },

  // Buscar projetos do usuário atual (com base em project_members)
  async getUserProjects(userId: string): Promise<ProjectWithRole[]> {
    console.log('[projectService] getUserProjects called, userId:', userId);
    if (!userId) {
      console.log('[projectService] No userId provided, returning empty array');
      return [];
    }

    try {
      console.log('[projectService] Querying project_members...');

      // Criar promise com timeout de 5 segundos
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout: Query demorou mais de 5 segundos')), 5000);
      });

      const queryPromise = supabase
        .from('project_members')
        .select(`
          role,
          is_favorited,
          project:projects(*)
        `)
        .eq('user_id', userId)
        .order('invited_at', { ascending: false });

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      console.log('[projectService] Query result:', { data, error });

      if (error) {
        // Ignorar AbortError - causado pelo React StrictMode
        if (error.message?.includes('AbortError')) {
          console.log('[projectService] AbortError ignorado');
          return [];
        }
        console.error('[projectService] Query error:', error);
        throw error;
      }

      if (!data) {
        console.log('[projectService] No data returned, returning empty array');
        return [];
      }

      // Formatar resposta dos memberships diretos
      const projects: ProjectWithRole[] = data.map((pm: Record<string, unknown>) => ({
        ...(pm.project as Project),
        userRole: pm.role,
        isFavorited: (pm.is_favorited as boolean) || false
      })) as ProjectWithRole[];

      // Buscar projetos de organizações do usuário (onde não é membro direto)
      try {
        const directProjectIds = projects.map(p => p.id);

        // Primeiro: buscar orgs do usuário
        const { data: userOrgMembers } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', userId);

        const userOrgIds = (userOrgMembers || []).map((m: { organization_id: string }) => m.organization_id);

        if (userOrgIds.length > 0) {
          // Buscar apenas projetos das orgs do usuário
          const { data: orgProjects } = await supabase
            .from('projects')
            .select('*')
            .in('organization_id', userOrgIds);

          if (orgProjects) {
            const orgOnlyProjects = (orgProjects as Project[])
              .filter(p => !directProjectIds.includes(p.id))
              .map(p => ({
                ...p,
                userRole: 'viewer' as const,
                isFavorited: false
              }));
            projects.push(...orgOnlyProjects);
          }
        }
      } catch {
        // Silently ignore - org projects are a bonus
      }

      console.log('[projectService] Returning', projects.length, 'projects');
      return projects;
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode
      if ((err as Error)?.message?.includes('AbortError')) {
        console.log('[projectService] getUserProjects AbortError ignorado');
        return [];
      }
      console.error('[projectService] getUserProjects error:', err);
      throw err;
    }
  },

  // Buscar todos os projetos com estatísticas de tarefas
  async getAllWithStats(): Promise<ProjectWithStats[]> {
    const { data: projects, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!projects || projects.length === 0) return [];

    // Buscar todas as tarefas de todos os projetos
    const { data: allTasks, error: tasksError } = await supabase
      .from('tasks')
      .select('project_id, progress, end_date, type')
      .in('project_id', projects.map((p: Record<string, unknown>) => p.id));

    if (tasksError) {
      console.error('Erro ao buscar tarefas:', tasksError);
      // Retorna projetos sem stats em caso de erro
      return projects.map((p: Record<string, unknown>) => ({ ...p, stats: { total: 0, completed: 0, overdue: 0 } })) as ProjectWithStats[];
    }

    // Calcular estatísticas por projeto
    const now = new Date();
    const statsMap: Record<string, ProjectStats> = {};

    (allTasks || []).forEach((task: Record<string, unknown>) => {
      // Ignorar marcos e grupos na contagem
      if (task.type === 'milestone' || task.type === 'group') return;

      if (!statsMap[task.project_id as string]) {
        statsMap[task.project_id as string] = { total: 0, completed: 0, overdue: 0 };
      }
      statsMap[task.project_id as string].total++;

      if ((task.progress as number) >= 100) {
        statsMap[task.project_id as string].completed++;
      }

      if (task.end_date && new Date(task.end_date as string) < now && (task.progress as number) < 100) {
        statsMap[task.project_id as string].overdue++;
      }
    });

    // Anexar stats aos projetos
    return projects.map((project: Record<string, unknown>) => ({
      ...project,
      stats: statsMap[project.id as string] || { total: 0, completed: 0, overdue: 0 }
    })) as ProjectWithStats[];
  },

  // Buscar projetos do usuário com estatísticas
  async getUserProjectsWithStats(userId: string): Promise<ProjectWithStats[]> {
    console.log('[projectService] getUserProjectsWithStats called, userId:', userId);

    try {
      const projects = await this.getUserProjects(userId);
      console.log('[projectService] getUserProjects returned:', projects?.length || 0, 'projects');

      if (!projects || projects.length === 0) {
        console.log('[projectService] No projects found, returning empty array');
        return [];
      }

      console.log('[projectService] Fetching tasks for stats...');

      // Buscar todas as tarefas de todos os projetos com timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout: Query de tarefas demorou mais de 5 segundos')), 5000);
      });

      const queryPromise = supabase
        .from('tasks')
        .select('project_id, progress, end_date, type')
        .in('project_id', projects.map(p => p.id));

      let allTasks: Record<string, unknown>[] = [];
      try {
        const { data, error: tasksError } = await Promise.race([queryPromise, timeoutPromise]);

        if (tasksError) {
          console.error('[projectService] Erro ao buscar tarefas:', tasksError);
          return projects.map(p => ({ ...p, stats: { total: 0, completed: 0, overdue: 0 } })) as ProjectWithStats[];
        }
        allTasks = (data || []) as Record<string, unknown>[];
      } catch (taskErr) {
        console.error('[projectService] Error fetching tasks:', taskErr);
        return projects.map(p => ({ ...p, stats: { total: 0, completed: 0, overdue: 0 } })) as ProjectWithStats[];
      }

      console.log('[projectService] Got', allTasks.length, 'tasks');

      // Calcular estatísticas por projeto
      const now = new Date();
      const statsMap: Record<string, ProjectStats> = {};

      allTasks.forEach((task: Record<string, unknown>) => {
        if (task.type === 'milestone' || task.type === 'group') return;

        if (!statsMap[task.project_id as string]) {
          statsMap[task.project_id as string] = { total: 0, completed: 0, overdue: 0 };
        }
        statsMap[task.project_id as string].total++;

        if ((task.progress as number) >= 100) {
          statsMap[task.project_id as string].completed++;
        }

        if (task.end_date && new Date(task.end_date as string) < now && (task.progress as number) < 100) {
          statsMap[task.project_id as string].overdue++;
        }
      });

      const result: ProjectWithStats[] = projects.map(project => ({
        ...project,
        stats: statsMap[project.id] || { total: 0, completed: 0, overdue: 0 }
      })) as ProjectWithStats[];

      console.log('[projectService] getUserProjectsWithStats returning', result.length, 'projects with stats');
      return result;
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode
      if ((err as Error)?.message?.includes('AbortError')) {
        console.log('[projectService] getUserProjectsWithStats AbortError ignorado');
        return [];
      }
      console.error('[projectService] getUserProjectsWithStats error:', err);
      throw err;
    }
  },

  // Buscar projeto por ID
  async getById(id: string): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Project;
  },

  // Criar novo projeto (adiciona criador como owner atomicamente via RPC)
  async create(project: Partial<Project>, userId: string): Promise<ProjectWithRole | null> {
    const settings = project.settings || {
      workDays: [1, 2, 3, 4, 5],
      hoursPerDay: 8,
      currency: 'BRL'
    };

    // RPC atômica: cria projeto + adiciona owner na mesma transação (SECURITY DEFINER)
    const { data: projectId, error: rpcError } = await supabase.rpc('create_project_returning_id', {
      p_name: project.name || 'Novo Projeto',
      p_description: project.description || '',
      p_settings: settings
    });

    if (rpcError) {
      if (rpcError.message?.includes('AbortError')) {
        console.log('[projectService] create AbortError ignorado');
        return null;
      }
      throw rpcError;
    }

    if (!projectId) {
      throw new Error(getCurrentCopy().common.serviceErrors.projectCreateNoId);
    }

    // Buscar o projeto completo (agora o usuário é membro, SELECT policy passa)
    const { data: newProject, error: fetchError } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (fetchError) throw fetchError;

    return newProject ? { ...newProject, userRole: 'owner' } as ProjectWithRole : null;
  },

  // Atualizar projeto
  async update(id: string, updates: Partial<Project>): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Project;
  },

  // Deletar projeto
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // Buscar o cronograma publicado por TOKEN.
  //
  // UMA rpc devolve projeto E tarefas, e a lista de colunas que cada tarefa carrega é
  // montada no SERVIDOR (`get_shared_project_data`, migration 20260828110000). Não existe
  // mais caminho anônimo que faça `select('*')` em `tasks`: até 08/2026 existia, e a RLS
  // devolvia zero linha para quem não tem sessão, então todo cliente que recebeu um link
  // desde 03/2026 abriu um cronograma vazio.
  //
  // Recorte de dado público é SQL, nunca CSS. Esconder coluna no front deixa o valor no
  // payload, e aqui o payload é lido por quem não é usuário do produto.
  async getSharedProjectData(shareToken: string, password: string | null = null): Promise<{ data: SharedProjectPayload | null; error: Error | null }> {
    const { data, error } = await supabase.rpc('get_shared_project_data', {
      p_share_token: shareToken,
      p_password: password
    });

    if (error) return { data: null, error };

    if (data?.error === 'not_found') {
      return { data: null, error: new Error(getCurrentCopy().common.serviceErrors.projectLinkExpired) };
    }
    if (data?.error === 'token_expired') {
      return { data: null, error: new Error('Este link de compartilhamento expirou.') };
    }
    if (data?.error === 'wrong_password') {
      return { data: null, error: new Error('wrong_password') };
    }
    if (data?.error === 'too_many_attempts') {
      return { data: null, error: new Error('too_many_attempts') };
    }
    if (data?.password_required) {
      return { data: { passwordRequired: true, name: data.project_name as string }, error: null };
    }

    return {
      data: {
        project: data.project as Record<string, unknown>,
        tasks: (data.tasks ?? []) as TaskRow[],
        team: (data.team ?? []) as TeamMember[],
        kanbanColumns: (data.kanbanColumns ?? null) as KanbanColumn[] | null,
        showAssignees: data.showAssignees === true
      },
      error: null
    };
  },

  // Publicar projeto e gerar token seguro
  async publishProject(projectId: string, publishSettings: Record<string, unknown> = {}, expiresInDays: number | null = null): Promise<{ data: Project; shareToken: string }> {
    // Buscar projeto atual para manter settings existentes e reutilizar token
    const { data: currentProject, error: fetchError } = await supabase
      .from('projects')
      .select('settings, share_token')
      .eq('id', projectId)
      .single();

    if (fetchError) throw fetchError;

    // Reutilizar token existente para não invalidar links já compartilhados
    const shareToken: string = (currentProject as Record<string, unknown>)?.share_token as string || crypto.randomUUID();
    const existingSettings: ProjectSettings = ((currentProject as Record<string, unknown>)?.settings || {}) as ProjectSettings;

    // Extrair senha antes de salvar (será tratada via RPC server-side)
    const { password, ...safePublishSettings } = publishSettings;

    // Calcular expiração do token
    let shareTokenExpiresAt: string | null = null;
    if (expiresInDays && expiresInDays > 0) {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);
      shareTokenExpiresAt = expiresAt.toISOString();
    }

    const { data, error } = await supabase
      .from('projects')
      .update({
        share_token: shareToken,
        share_token_expires_at: shareTokenExpiresAt,
        settings: {
          ...existingSettings,
          publish: {
            ...existingSettings.publish,
            enabled: true,
            publishedAt: new Date().toISOString(),
            ...safePublishSettings
          }
        },
        updated_at: new Date().toISOString()
      })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;

    // Se tem senha, salvar via RPC (hash server-side)
    if (password) {
      await supabase.rpc('set_share_password', {
        p_project_id: projectId,
        p_password: password
      });
    }

    return { data: data as Project, shareToken };
  },

  // Alternar favorito de um projeto para o usuário (via RPC para bypassar RLS)
  async toggleFavorite(projectId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('toggle_project_favorite', {
      p_project_id: projectId
    });
    if (error) throw error;
    return data as boolean;
  },

  // Despublicar projeto e revogar token
  async unpublishProject(projectId: string): Promise<{ data: Project }> {
    // Buscar projeto atual para manter settings existentes
    const { data: currentProject, error: fetchError } = await supabase
      .from('projects')
      .select('settings')
      .eq('id', projectId)
      .single();

    if (fetchError) throw fetchError;

    const existingSettings: ProjectSettings = ((currentProject as Record<string, unknown>)?.settings || {}) as ProjectSettings;

    const { data, error } = await supabase
      .from('projects')
      .update({
        share_token: null,
        settings: {
          ...existingSettings,
          publish: {
            enabled: false
          }
        },
        updated_at: new Date().toISOString()
      })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return { data: data as Project };
  }
};
